/**
 * Shared UI parts for long-form sales pages (Offer, Services, future tiers).
 * Extracted from Offer.jsx and Services.jsx to keep them DRY.
 *
 * Includes the dark-luxe design language: cream/espresso/gold palette,
 * Fraunces display headlines, framer-motion fade-up scroll reveals,
 * and the polymorphic MegaCTA button (renders as <Link>, <a>, or <button>
 * depending on which prop is supplied — `to`, `href`/anchor `to`, or
 * `onClick`).
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

// ============ Animation ============
export const fadeUp = {
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-60px" },
    transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] },
};

// ============ Section wrappers ============
export const Section = ({ id, dark, className = "", children }) => (
    <section
        id={id}
        className={`py-24 md:py-32 px-6 md:px-12 ${dark ? "bg-[#1A0F0A] text-cafe-cream" : "bg-cafe-cream text-cafe-espresso"} ${className}`}
    >
        <div className="max-w-7xl mx-auto">{children}</div>
    </section>
);

export const Kicker = ({ children, dark }) => (
    <div className={`inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.32em] mb-6 ${dark ? "text-[#D4A574]" : "text-cafe-olive"}`}>
        <span className={`h-px w-8 ${dark ? "bg-[#D4A574]" : "bg-cafe-olive"}`} />
        {children}
    </div>
);

// ============ MegaCTA ============
// Polymorphic large CTA. Choose ONE of:
//   • onClick={fn}                → renders as <button>
//   • to="/internal-path"         → renders as <Link>
//   • to="https://external"       → renders as <a target="_blank">
//   • to="#anchor"                → renders as <a> for in-page scroll
export function MegaCTA({
    to, onClick, variant, testid, headline, anchorOld, price, suffix = "",
    subline, saveBadge,
}) {
    const isPrimary = variant === "primary";
    const isExternal = typeof to === "string" && /^https?:\/\//i.test(to);
    const isAnchor = typeof to === "string" && to.startsWith("#");
    const className = `group relative overflow-hidden inline-flex flex-col items-center justify-center text-center
        w-full sm:w-auto sm:min-w-[290px] min-h-[140px] sm:min-h-[150px]
        px-8 sm:px-12 py-6 sm:py-7 rounded-2xl
        transition-all duration-300 hover:-translate-y-0.5 active:translate-y-0
        ${isPrimary
            ? "bg-cafe-espresso hover:bg-cafe-olive text-cafe-cream"
            : "bg-cafe-cream text-cafe-espresso border border-cafe-espresso hover:bg-cafe-espresso hover:text-cafe-cream"
        }`;

    const inner = (
        <>
            {isPrimary && saveBadge && (
                <span className="absolute top-3 right-3 bg-cafe-cream text-cafe-espresso text-[10px] tracking-[0.18em] uppercase font-medium px-2.5 py-1 rounded-full">
                    {saveBadge}
                </span>
            )}
            <span className={`relative text-[10px] sm:text-[11px] tracking-[0.28em] uppercase font-medium mb-1.5 ${isPrimary ? "text-cafe-cream/70" : "text-cafe-espresso/60"}`}>
                {headline}
            </span>
            <span className="relative flex items-baseline gap-3">
                {anchorOld && (
                    <span className={`line-through text-base sm:text-lg ${isPrimary ? "text-cafe-cream/40" : "text-cafe-espresso/40"}`}>
                        {anchorOld}
                    </span>
                )}
                <span className="font-serif text-3xl sm:text-4xl md:text-5xl tracking-[-0.015em] leading-none" style={{ fontWeight: 500 }}>{price}</span>
                {suffix && (
                    <span className={`text-base sm:text-lg font-light ${isPrimary ? "text-cafe-cream/70" : "text-cafe-espresso/60"}`}>
                        {suffix}
                    </span>
                )}
            </span>
            <span className="relative mt-2.5 flex items-center gap-2 text-[13.5px] font-medium">
                {subline}
                <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" strokeWidth={1.5} />
            </span>
        </>
    );

    if (onClick) {
        return (
            <button type="button" onClick={onClick} data-testid={testid} className={className}>
                {inner}
            </button>
        );
    }
    if (isExternal) {
        return (
            <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testid} className={className}>
                {inner}
            </a>
        );
    }
    if (isAnchor) {
        return (
            <a href={to} data-testid={testid} className={className}>
                {inner}
            </a>
        );
    }
    return (
        <Link to={to} data-testid={testid} className={className}>
            {inner}
        </Link>
    );
}
